import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Chip,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import toast from 'react-hot-toast'
import {
  createAnomalyTicket,
  fetchAnomalyTickets,
  notifyTicketsChanged,
  ticketRefreshEventName,
  updateAnomalyTicket
} from 'utils/anomalyTickets'

const STATUS_ORDER = ['open', 'in_progress', 'resolved']

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved'
}

const STATUS_COLORS = {
  open: 'warning',
  in_progress: 'primary',
  resolved: 'success'
}

const buildColumnsFromTickets = tickets =>
  STATUS_ORDER.reduce(
    (acc, status) => {
      acc[status] = tickets.filter(ticket => ticket.status === status)
      return acc
    },
    { open: [], in_progress: [], resolved: [] }
  )

const KanbanBoard = () => {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createForm, setCreateForm] = useState({ title: '', description: '', assignee: '' })
  const [createErrors, setCreateErrors] = useState({})
  const [creating, setCreating] = useState(false)
  const [pendingResolution, setPendingResolution] = useState(null)
  const [resolutionReason, setResolutionReason] = useState('')
  const [resolutionSubmitting, setResolutionSubmitting] = useState(false)
  const [ticketMutations, setTicketMutations] = useState({})

  const refreshTickets = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const remoteTickets = await fetchAnomalyTickets()
      setTickets(remoteTickets)
    } catch (err) {
      setError(err?.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshTickets()
  }, [refreshTickets])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handler = () => refreshTickets()
    window.addEventListener(ticketRefreshEventName, handler)
    return () => window.removeEventListener(ticketRefreshEventName, handler)
  }, [refreshTickets])

  const columns = useMemo(() => buildColumnsFromTickets(tickets), [tickets])

  const columnMeta = useMemo(() => {
    return STATUS_ORDER.map(status => ({
      status,
      label: STATUS_LABELS[status],
      color: STATUS_COLORS[status],
      tickets: columns[status] || []
    }))
  }, [columns])

  const setTicketMutation = useCallback((ticketId, active) => {
    setTicketMutations(prev => {
      const next = { ...prev }
      if (active) {
        next[ticketId] = true
      } else {
        delete next[ticketId]
      }
      return next
    })
  }, [])

  const upsertTicket = useCallback(updatedTicket => {
    setTickets(prev => {
      const index = prev.findIndex(ticket => ticket.id === updatedTicket.id)
      if (index === -1) {
        return [updatedTicket, ...prev]
      }
      const clone = [...prev]
      clone[index] = updatedTicket
      return clone
    })
  }, [])

  const handleStatusChange = useCallback(
    async (ticket, nextStatus, resolutionNote = '') => {
      if (!ticket || ticket.status === nextStatus) return

      const updates = { status: nextStatus }
      if (nextStatus === 'resolved') {
        updates.resolutionComment = resolutionNote || ticket.resolutionComment || ''
      }

      setTicketMutation(ticket.id, true)
      const processingToastId = toast.loading('Processing ticket...')
      try {
        const updated = await updateAnomalyTicket(ticket.id, updates)
        upsertTicket(updated)
        notifyTicketsChanged()
        toast.dismiss(processingToastId)
        toast.success(`Ticket moved to ${STATUS_LABELS[nextStatus]}`)
        return updated
      } catch (error) {
        toast.dismiss(processingToastId)
        const message = error?.message || 'Failed to update ticket'
        toast.error(message)
        throw error
      } finally {
        setTicketMutation(ticket.id, false)
      }
    },
    [setTicketMutation, upsertTicket]
  )

  const handleDragEnd = useCallback(
    async result => {
      const { source, destination } = result

      if (!destination) return
      if (source.droppableId === destination.droppableId) return

      const sourceTickets = columns[source.droppableId] || []
      const movedTicket = sourceTickets[source.index]

      if (!movedTicket) return

      if (destination.droppableId === 'resolved' && source.droppableId !== 'resolved') {
        setPendingResolution({
          ticket: movedTicket
        })
        setResolutionReason('')
        return
      }

      try {
        await handleStatusChange(movedTicket, destination.droppableId)
      } catch (error) {
        // Errors are surfaced via toast, no further handling
      }
    },
    [columns, handleStatusChange]
  )

  const handleConfirmResolution = async () => {
    if (!pendingResolution || !resolutionReason.trim()) return
    setResolutionSubmitting(true)
    try {
      await handleStatusChange(pendingResolution.ticket, 'resolved', resolutionReason.trim())
      setPendingResolution(null)
      setResolutionReason('')
    } catch (error) {
      // handled in handleStatusChange
    } finally {
      setResolutionSubmitting(false)
    }
  }

  const handleCancelResolution = () => {
    setPendingResolution(null)
    setResolutionReason('')
  }

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false)
    setCreateForm({ title: '', description: '', assignee: '' })
    setCreateErrors({})
  }

  const handleCreateFieldChange = event => {
    const { name, value } = event.target
    setCreateForm(prev => ({ ...prev, [name]: value }))
  }

  const validateCreateForm = () => {
    const errors = {}
    if (!createForm.title.trim()) errors.title = 'Title is required'
    if (!createForm.description.trim()) errors.description = 'Description is required'
    return errors
  }

  const handleCreateTicket = async () => {
    const errors = validateCreateForm()
    setCreateErrors(errors)
    if (Object.keys(errors).length) return

    setCreating(true)
    let processingToastId = null
    try {
      processingToastId = toast.loading('Processing ticket...')
      const payload = {
        title: createForm.title.trim(),
        description: createForm.description.trim(),
        assignee: createForm.assignee.trim() || null,
        status: 'open'
      }
      const created = await createAnomalyTicket(payload)
      setTickets(prev => [created, ...prev])
      notifyTicketsChanged()
      refreshTickets()
      toast.dismiss(processingToastId)
      toast.success('Ticket created')
      handleCreateDialogClose()
    } catch (error) {
      if (processingToastId) toast.dismiss(processingToastId)
      const message = error?.message || 'Failed to create ticket'
      toast.error(message)
    } finally {
      setCreating(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography variant='h4' fontWeight={600}>
            Anomaly Kanban
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Track anomaly tickets as they move from detection to resolution.
          </Typography>
        </Box>
        <Button variant='contained' onClick={() => setCreateDialogOpen(true)}>
          Create Ticket +
        </Button>
      </Stack>

      {error && <Alert severity='error'>{error}</Alert>}

      {loading && tickets.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Grid container spacing={3}>
            {columnMeta.map(({ status, label, color, tickets }) => (
              <Grid item xs={12} md={4} key={status}>
                <Droppable droppableId={status}>
                  {provided => (
                    <Paper
                      elevation={3}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        p: 2,
                        minHeight: 420,
                        backgroundColor: theme => theme.palette.grey[50],
                        borderTop: theme => `4px solid ${theme.palette[color].main}`
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant='h6' fontWeight={600} color={theme => theme.palette[color].dark}>
                            {label}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {tickets.length} tickets
                          </Typography>
                        </Box>

                        <Stack spacing={2}>
                          {tickets.map((ticket, index) => (
                            <Draggable
                              draggableId={String(ticket.id)}
                              index={index}
                              key={ticket.id}
                              // isDragDisabled={Boolean(ticketMutations[ticket.id])}
                            >
                              {draggableProvided => (
                                <Card
                                  variant='outlined'
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  sx={{
                                    borderColor: theme => theme.palette[color].light,
                                    opacity: ticketMutations[ticket.id] ? 0.6 : 1
                                  }}
                                >
                                  <CardContent>
                                    <Stack direction='row' alignItems='center' justifyContent='space-between' mb={1}>
                                      <Typography variant='subtitle2' fontWeight={600}>
                                        TCK-{String(ticket.id).padStart(4, '0')}
                                      </Typography>
                                      <Chip
                                        label={STATUS_LABELS[ticket.status]}
                                        size='small'
                                        color={STATUS_COLORS[ticket.status] || 'default'}
                                      />
                                    </Stack>
                                    <Typography variant='body1' fontWeight={500} gutterBottom>
                                      {ticket.title}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                      {ticket.summary || ticket.description || 'No description provided'}
                                    </Typography>
                                    {ticket.assignee && (
                                      <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ display: 'block', mt: 1 }}
                                      >
                                        Assignee: {ticket.assignee}
                                      </Typography>
                                    )}
                                  {ticket.resolutionComment && (
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ display: 'block', mt: 1 }}
                                    >
                                      Resolution: {ticket.resolutionComment}
                                    </Typography>
                                  )}
                                    {ticketMutations[ticket.id] && (
                                      <Stack direction='row' alignItems='center' spacing={1} mt={1}>
                                        <CircularProgress size={16} />
                                        <Typography variant='caption' color='text.secondary'>
                                          Updating...
                                        </Typography>
                                      </Stack>
                                    )}
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}

                          {tickets.length === 0 && (
                            <Box
                              sx={{
                                border: '1px dashed',
                                borderColor: theme => theme.palette.grey[300],
                                borderRadius: 1,
                                p: 3,
                                textAlign: 'center',
                                color: 'text.secondary'
                              }}
                            >
                              No tickets here yet.
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Droppable>
              </Grid>
            ))}
          </Grid>
        </DragDropContext>
      )}

      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} fullWidth maxWidth='sm'>
        <DialogTitle>Create Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <TextField
              label='Title'
              name='title'
              value={createForm.title}
              onChange={handleCreateFieldChange}
              error={Boolean(createErrors.title)}
              helperText={createErrors.title}
              autoFocus
            />
            <TextField
              label='Description'
              name='description'
              value={createForm.description}
              onChange={handleCreateFieldChange}
              error={Boolean(createErrors.description)}
              helperText={createErrors.description}
              multiline
              minRows={3}
            />
            <TextField
              label='Assignee'
              name='assignee'
              value={createForm.assignee}
              onChange={handleCreateFieldChange}
              placeholder='Optional'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={creating}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleCreateTicket} disabled={creating}>
            {creating ? 'Creating...' : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingResolution)} onClose={handleCancelResolution} fullWidth maxWidth='sm'>
        <DialogTitle>Resolution Details</DialogTitle>
        <DialogContent>
          <Typography variant='body2' mb={2}>
            Add a short note explaining how this anomaly was resolved.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label='Resolution reason'
            value={resolutionReason}
            onChange={e => setResolutionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelResolution}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleConfirmResolution}
            disabled={!resolutionReason.trim() || resolutionSubmitting}
          >
            {resolutionSubmitting ? 'Saving...' : 'Save & Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default KanbanBoard
